import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Stack, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import dayjs from "dayjs";
import { NormalButton } from "../../../components/ui/CommonReusables";
import RichEditor from "../../../components/rich-editor";
import { dialogCloseSx, primaryButtonSx } from "../../CurriculumManagementAdmin/components/buttonStyles";
import { isEmptyHtml } from "../../CurriculumManagementAdmin/utils/html";
import { AttendanceChip } from "./AttendanceMarkers";

const Description = ({ html, emptyLabel }) =>
	isEmptyHtml(html) ? (
		<Typography variant="body2" color="text.secondary">
			{emptyLabel}
		</Typography>
	) : (
		// The editor renders its own saved markup, so the agent reads exactly what was authored.
		<RichEditor value={html} readOnly />
	);

/**
 * Read-only look at a curriculum and every training under it, with the agent's own attendance
 * marked per session. The agent-side counterpart to the admin's ViewCurriculumDialog — no roster,
 * since one agent has no business seeing who else was in the room.
 */
const CurriculumPreviewDialog = ({ open, onClose, curriculum, trainings }) => {
	if (!curriculum) return null;

	const attendedCount = trainings.filter((training) => training.attended).length;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Elementor sizes the paper to its content and ignores MUI's maxWidth, so the sm
						// breakpoint is pinned here. Width stays margin-aware so it never overflows.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "600px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							{curriculum.title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{trainings.length} {trainings.length === 1 ? "training" : "trainings"} · {attendedCount} attended
						</Typography>
					</Box>

					<IconButton onClick={onClose} size="small" aria-label="Close curriculum preview" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ width: "100%" }}>
				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Description
				</Typography>

				<Box sx={{ mt: 0.5, mb: 2 }}>
					<Description html={curriculum.description} emptyLabel="No description provided." />
				</Box>

				<Divider sx={{ mb: 2 }} />

				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Trainings ({trainings.length})
				</Typography>

				{trainings.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						No trainings added yet.
					</Typography>
				) : (
					<Stack spacing={1.5} sx={{ mt: 1.5 }}>
						{trainings.map((training) => (
							<Box
								key={training.id}
								sx={{
									border: "1px solid",
									// Attended sessions carry the badge green through the whole card.
									borderColor: training.attended ? "#B7EBD5" : "#D6E6F5",
									borderRadius: 2,
									p: 2,
									backgroundColor: training.attended ? "#F6FFFB" : "#FBFDFF",
								}}
							>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									alignItems={{ xs: "flex-start", sm: "center" }}
									justifyContent="space-between"
									spacing={1}
									sx={{ mb: 0.5 }}
								>
									<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
										<SchoolOutlinedIcon fontSize="small" sx={{ color: training.attended ? "#0F7A53" : "#3A7DC9", flexShrink: 0 }} />
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={training.title}>
												{training.title}
											</Typography>
											<Typography variant="caption" color="text.secondary" noWrap>
												{dayjs(training.date).format("MMM D, YYYY")}
											</Typography>
										</Box>
									</Stack>

									<Box sx={{ flexShrink: 0 }}>
										<AttendanceChip attended={training.attended} attendedAt={training.attended_at} />
									</Box>
								</Stack>

								<Description html={training.description} emptyLabel="No description provided." />
							</Box>
						))}
					</Stack>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<NormalButton onClick={onClose} sx={primaryButtonSx}>
					Close
				</NormalButton>
			</DialogActions>
		</Dialog>
	);
};

export default CurriculumPreviewDialog;
